import type { UserOffice } from '@artham/shared';

import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { useGpsPermission } from 'src/hooks/use-gps-permission';
import { useCurrentLocation } from 'src/hooks/use-current-location';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = {
  value?: UserOffice;
  onChange: (value: UserOffice) => void;
};

const ADDRESS_FIELDS = ['address', 'area', 'city', 'state', 'pincode'] as const;
type AddressField = (typeof ADDRESS_FIELDS)[number];

/** Renders the resolved address as one human line, skipping blanks. */
function formatAddress(office: UserOffice | undefined): string {
  if (!office) return '';
  return [office.address, office.area, office.city, office.state, office.pincode]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * Office location, captured in one tap.
 *
 * Replaces OfficeLocationPicker, which showed a 450px map centred on the
 * geographic centroid of India. Because that default was always populated, a
 * dealer who never touched the map still submitted coordinates - pointing at a
 * field near Nagpur. Here `location` is only ever set from a real GPS fix, so
 * an absent location is honestly absent.
 */
export function OfficeLocationField({ value, onChange }: Props) {
  const { t } = useTranslate('auth');

  const gps = useGpsPermission();
  const { getCurrentLocation } = useCurrentLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const hasLocation = !!value?.location;
  const addressLine = formatAddress(value);

  // Keep the editor open while the pincode is bad, so the field carrying the
  // error cannot be collapsed out of sight.
  const pincodeInvalid = !!value?.pincode && !/^\d{6}$/.test(value.pincode);
  const editorOpen = editing || pincodeInvalid;

  const patch = useCallback(
    (fields: Partial<UserOffice>) => {
      onChange({ ...(value ?? {}), ...fields });
    },
    [onChange, value]
  );

  const handleUseMyLocation = useCallback(async () => {
    setError(null);

    if (gps.isNative && gps.isGpsDenied) {
      gps.openAppSettings();
      return;
    }

    if (gps.isNative && gps.permissionStatus === 'prompt') {
      const granted = await gps.requestPermission();
      if (!granted) {
        setError(t('officeLocation.permissionDenied'));
        return;
      }
    }

    setLoading(true);
    try {
      const result = await getCurrentLocation();
      if (!result) {
        setError(t('officeLocation.lookupFailed'));
        return;
      }

      // Reverse geocode is best-effort. Coordinates are the part that matters,
      // so keep them even when Nominatim returns nothing usable, and never
      // overwrite something the dealer typed with a blank.
      patch({
        location: result.coordinates,
        address: result.address || value?.address,
        area: result.area || value?.area,
        city: result.city || value?.city,
        state: result.state || value?.state,
        country: result.country || value?.country || 'India',
        pincode: result.pincode || value?.pincode,
      });

      // Nothing legible came back - open the fields so they can type it.
      if (!result.address && !result.area && !result.city) {
        setEditing(true);
      }
    } catch {
      setError(t('officeLocation.lookupFailed'));
    } finally {
      setLoading(false);
    }
  }, [gps, getCurrentLocation, patch, value, t]);

  return (
    <Stack spacing={1.5}>
      {!hasLocation && !addressLine ? (
        <LoadingButton
          fullWidth
          size="large"
          variant="outlined"
          loading={loading}
          onClick={handleUseMyLocation}
          startIcon={<Iconify icon="mingcute:location-fill" />}
        >
          {t('officeLocation.useMyLocation')}
        </LoadingButton>
      ) : (
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.neutral',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Iconify
              icon="custom:location-fill"
              width={22}
              sx={{ color: 'primary.main', flexShrink: 0, mt: 0.25 }}
            />
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2">
                {addressLine || t('officeLocation.pinnedNoAddress')}
              </Typography>
              {value?.location && (
                <Typography variant="caption" color="text.secondary">
                  {t('officeLocation.selected', {
                    lat: value.location.lat.toFixed(5),
                    lng: value.location.lng.toFixed(5),
                  })}
                </Typography>
              )}
            </Stack>
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              onClick={() => setEditing((prev) => !prev)}
              sx={{ flexShrink: 0 }}
            >
              {editorOpen ? t('common.done') : t('common.edit')}
            </Link>
          </Stack>

          {!loading && (
            <Button
              size="small"
              variant="text"
              onClick={handleUseMyLocation}
              startIcon={<Iconify icon="mingcute:location-fill" width={16} />}
              sx={{ mt: 1, ml: -0.5 }}
            >
              {t('officeLocation.updateLocation')}
            </Button>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="warning" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Typed address. Hidden until asked for - the geocode usually gets it
          right, and six always-visible fields is what made this step feel long. */}
      <Collapse in={editorOpen} unmountOnExit>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {ADDRESS_FIELDS.map((field: AddressField) => (
            <TextField
              key={field}
              fullWidth
              size="small"
              label={t(`officeLocation.${field}`)}
              placeholder={t(`officeLocation.${field}Placeholder`)}
              value={value?.[field] ?? ''}
              onChange={(e) => patch({ [field]: e.target.value })}
              {...(field === 'address' && { multiline: true, rows: 2 })}
              {...(field === 'pincode' && {
                inputProps: { maxLength: 6, inputMode: 'numeric', pattern: '[0-9]{6}' },
                error: pincodeInvalid,
                helperText: pincodeInvalid ? t('officeLocation.pincodeInvalid') : undefined,
              })}
            />
          ))}

          {!hasLocation && (
            <LoadingButton
              fullWidth
              variant="outlined"
              loading={loading}
              onClick={handleUseMyLocation}
              startIcon={<Iconify icon="mingcute:location-fill" />}
            >
              {t('officeLocation.useMyLocation')}
            </LoadingButton>
          )}
        </Stack>
      </Collapse>

      {gps.isGpsDenied && gps.isNative && (
        <Button
          size="small"
          variant="text"
          onClick={gps.openAppSettings}
          startIcon={<Iconify icon="solar:settings-bold" width={16} />}
        >
          {t('officeLocation.locationDeniedOpenSettings')}
        </Button>
      )}
    </Stack>
  );
}
